<template>
    <div class="sponsor-search">
        <ns-spinner id="ns-accounts-global-loader" src="/etc/designs/hello/packaged/img/spinner.gif"/>
        <h1 v-if="title">{{title}}</h1>
        <p v-if="info && showTransferItemsInformation" class="sponsor-info">
          <ns-icon class="tooltip-icon-blue" icon-name="icon-info-filled" :color="blue"/> {{info}}
        </p>
        <p class="sponsor-title">{{label}}</p>
        <v-text-field v-model="searchString" v-on:keyup.enter="search"/>
        <button
          id='searchButton'
          :disabled="searchString.trim().length < 2 || searchInProgress"
          class="primary-solid search-button"
          @click="search">
          <CustomSpinner v-if="searchInProgress"/> {{ buttonText }}
        </button>
        <template v-if="searchResults.length > 0">
          <div class="search-results">
              <div class="legend-content">
                  {{resultsText}}
              </div>
          </div>
          <div v-for="(person, index) in searchResults" :key="index">
              <div
                class="sponsor-search-avatar-holder"
                @click="selectDistributor(person)"
              >
                <ns-modal-local
                  v-model="showIneligibleTransferModal"
                  :value="true"
                  :icon="true"
                  :small="true"
                  :info="true"
                />
                <div class="sponsor-details">
                  <div
                    v-if="person.localName"
                    class="sponsor-name"
                    :class="{ disabled: !validateMarketTransferAllowed(person.market)}"
                  >
                    {{person.localName}}
                  </div>
                  <div
                    v-else
                    class="sponsor-name"
                    :class="{ disabled: !validateMarketTransferAllowed(person.market)}"
                  >
                    {{person.name}}
                  </div>
                  <ns-icon
                    v-if="!validateMarketTransferAllowed(person.market)"
                    icon-name="icon-info"
                    class="nuskin-purple"
                    size="icon-lg"
                  />
                  <ns-icon
                    v-else
                    icon-name="icon-chevron-right"
                    class="nuskin-purple"
                    size="icon-lg"
                  />
                </div>
              </div>
          </div>
        </template>
        <div v-if="showNoSearchResult">
            <div class="sponsor-search-result-error"><p>{{searchError}}</p></div>
        </div>
    </div>
</template>
<script>
import { VTextField } from 'vuetify/lib'
import { VGDistInfoService } from '@nuskin/ns-account'
import { NsIcon } from '@nuskin/ns-icon'
import CustomSpinner from './CustomSpinner.vue'

export default {
  name: 'NsDistributorSearch',
  components: {
    CustomSpinner,
    VTextField,
    NsIcon,
  },
  props: {
    title: String,
    info: {
      type: String,
      default: 'Tooltip type message',
    },
    label: String,
    buttonText: String,
    resultsText: String,
    value: Object,
    searchError: {
      type: String,
      default: 'No Results',
    },
    pageSize: {
      type: Number,
      default: 30,
    },
    level: {
      type: Number,
    },
    product: {
      type: Object,
      default: () => ({ sku: null, isExclusive: false }),
    },
    qualifications: Object,
    restrictedMarkets: Object,
    showTransferItemsInformation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchString: '',
      clickedSearch: false,
      searchData: { list: [] },
      searchResults: [],
      pageNumber: 1,
      defaultProfileImgUrl: '',
      showNoSearchResult: false,
      showIneligibleTransferModal: false,
      searchInProgress: false,
    }
  },
  methods: {
    search() {
      this.showNoSearchResult = false
      this.searchResults = []
      // Remove all special characters from search
      this.searchString = this.searchString.replace(/[&/\\#,[\]+()$~%+=|.":*?!@^<>{}]/g, '').trim()
      if (this.searchString && this.searchString.trim().length >= 2 && this.searchInProgress == false) {
        this.searchInProgress = true
        this.searchDownlineList(this.searchString)
      }
    },
    async searchDownlineList(searchTerm) {
      try {
        await VGDistInfoService.searchDownline(searchTerm).then((results) => {
          // Remove the BB and TEBB accounts from the results
          results = results.filter((person) => (!person.distId || (!person.distId.endsWith('BB') && !person.distId.endsWith('BC'))))
          // Sorts results by name
          results.sort((a, b) => {
            const aName = a && a.name ? a.name.toLowerCase() : a.localName.toLowerCase()
            const bName = b && b.name ? b.name.toLowerCase() : b.localName.toLowerCase()
            const aGreaterThanB = (aName > bName) ? 1 : 0
            return (aName < bName) ? -1 : aGreaterThanB
          })
          this.searchResults = results
        })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err)
      }
      this.searchInProgress = false
      this.showNoSearchResult = this.searchResults.length === 0
    },
    selectDistributor(person) {
      if (!this.validateMarketTransferAllowed(person.market)) {
        this.$emit('selectedUserOutOfMarket', person)
      } else {
        this.$emit('input', person)
      }
    },
    validateMarketTransferAllowed(market) {
      if (this.restrictedMarkets && Object.keys(this.restrictedMarkets).length > 0) {
        let countOfRestrictedProducts = 0
        Object.keys(this.restrictedMarkets).forEach((item) => {
          if (Array.isArray(this.restrictedMarkets[item]) && this.restrictedMarkets[item].includes(market)) {
            countOfRestrictedProducts++
          }
        })
        if (countOfRestrictedProducts > 0 && countOfRestrictedProducts == Object.keys(this.qualifications).length) {
          return false
        }
      }

      return true
    },
  },
}
</script>
<style lang="scss" scoped>
  @import '~@nuskin/ns-core-styles/src/scss/global.mixins';
  @import '~@nuskin/ns-core-styles/src/scss/vuetifyOverrides';

  .sponsor-search {
    max-width: 500px;
  }

  .nuskin-purple {
    color: #7123d9;
  }

  .tooltip-icon-blue {
    color: #35b2f0;
    margin-right: 5px;
  }

  .sponsor-info {
    display: flex;
    padding-top: 15px;
  }

  .sponsor-title {
    padding-top: 15px;
  }

  .sponsor-search button, .sponsor-search .v-input__control, .sponsor-search input {
      width: 100%;
      max-width: 400px;
      box-sizing: border-box;
  }

  .sponsor-search-form-group {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      align-content: flex-start;
      width: 500px;
  }

  .search-button {
      width: 30%;
  }

  .sponsor-search-avatar-holder {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      cursor: pointer;
      position: relative;
  }

  .sponsor-search-avatar-holder:hover {
      background-color: var(--color-background-1);
  }

  .sponsor-search-avatar-holder .icon {
      position: absolute;
      right: 10px;
      color: var(--mermaid);
  }

  .sponsor-avatar {
      width: 100px;
      text-align: center;
  }

  .sponsor-details {
      padding: 15px 10px;
      border-bottom: 1px solid var(--color-gray-3);
      width: 100%;
      display: flex;
      justify-content: space-between;
  }

  .sponsor-name {
      font-family: Proxima Nova;
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      line-height: 24px;
      color: #272525;
  }

  .profile-image {
      border-radius: 50%;
      max-width: 40px;
      width: 40px;
      height: 40px;
      max-height: 40px;
      flex-basis: auto;
      align-self: flex-start;
  }

  .search-results {
      margin: 10px 0px;
  }
  h1 {
      @include title-thin();
      text-align: center;
      margin-bottom: 30px;
  }
  .legend-content {
      margin-top:30px;
      margin-bottom: 20px;
      text-transform: uppercase;
      font-weight: var(--font-weight-bold);
      color: var(--blackhole);
  }

  .transfer-display-item {
    text-align: left;
  }

  @media (max-width: 500px) {
      .sponsor-search {
          width: 300px;
      }
      .legend-content {
          text-align: center;
      }
      .sponsor-search-form-group {
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          align-items: center;
          align-content: flex-start;
          width: 100%;
      }
      input[type="text"] {
          font-size: 14px;
          padding: 0px;
          width: 100%;
          height: 41px;
      }

      .search-button {
          margin-top: 10px;
          width: 100%;
          margin-left: 0px;
      }
  }

  .disabled {
    color: #BBBCBC;
  }

</style>
